import {
  Alert,
  Anchor,
  Badge,
  Button,
  Card,
  Checkbox,
  Grid,
  Group,
  MultiSelect,
  ScrollArea,
  Select,
  Stack,
  Table,
  Text,
  Textarea,
  TextInput,
  Title,
} from '@mantine/core';
import {
  RISQ_PREP_TIMINGS,
  VISIBLE_WORK_TYPES,
  type RisqPrepTiming,
  type RisqWorkType,
} from '@opti-sea/risq';
import { useQuery } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useParams } from 'react-router';
import { useWorkplace } from '../../../core/access';
import { unwrap, useDb } from '../../../core/supabase';
import { PageTitle, QueryState, usePositionNames } from '../../../ui/components';
import { useContent, useLayers, useRpc } from '../data';
import {
  companyPositionsOf,
  flagsOf,
  vesselPositionsOf,
  type Layers,
  type Question,
} from '../model';
import { LayerHistory } from './LayerHistory';

function useEditorData(question: Question | null) {
  const db = useDb();
  const { company } = useWorkplace();
  return useQuery({
    queryKey: ['editor', company.id, question?.key ?? ''],
    enabled: question !== null,
    queryFn: async () => {
      const key = question?.key ?? '';
      const [points, companyPoints, notes] = await Promise.all([
        db
          .from('risq_wtc_items')
          .select('id, item_key, sort_order, text_en')
          .eq('question_id', question?.id ?? '')
          .is('deleted_at', null)
          .order('sort_order'),
        db
          .from('risq_company_wtc_items')
          .select('id, item_key, is_own, text_en, hidden, sort_order')
          .eq('company_id', company.id)
          .eq('question_key', key)
          .is('deleted_at', null)
          .order('sort_order'),
        db
          .from('risq_company_notes')
          .select('id, kind, text, origin, sort_order')
          .eq('company_id', company.id)
          .eq('question_key', key)
          .is('deleted_at', null)
          .order('sort_order'),
      ]);
      return { points: unwrap(points), companyPoints: unwrap(companyPoints), notes: unwrap(notes) };
    },
  });
}

function SaveError({ error }: { error: Error | null }) {
  const { t } = useTranslation('dashboard');
  return error === null ? null : (
    <Alert color="red" role="alert">
      {t('common.saveFailed', { message: error.message })}
    </Alert>
  );
}

const refreshLayers = (companyId: string, key: string) =>
  [
    ['layers', companyId],
    ['editor', companyId, key],
    ['history', companyId, key],
  ] as const;

function PositionsEditor({ question, layers }: { question: Question; layers: Layers }) {
  const { t } = useTranslation('dashboard');
  const { company, vessels, access } = useWorkplace();
  const { name, list } = usePositionNames();
  const options = access.positions.map((p) => ({ value: p.code, label: name(p.code) }));
  const set = useRpc('risq_set_question_positions', refreshLayers(company.id, question.key));
  const revert = useRpc('risq_revert_question_positions', refreshLayers(company.id, question.key));
  const [drafts, setDrafts] = useState<Record<string, string[]>>({});
  const rows = [
    {
      id: 'company',
      vesselId: null,
      label: t('editor.wholeCompany'),
      now: companyPositionsOf(question, layers),
      own: layers.companyPositions.has(question.key),
    },
    ...vessels.map((v) => ({
      id: v.id,
      vesselId: v.id,
      label: v.name,
      now: vesselPositionsOf(question, layers, v.id),
      own: layers.vesselPositions.get(v.id)?.has(question.key) ?? false,
    })),
  ];
  return (
    <Card withBorder>
      <Stack>
        <Title order={2} size="h3">
          {t('editor.positionsTitle')}
        </Title>
        <Text size="sm">{t('editor.positionsHint')}</Text>
        <Table.ScrollContainer minWidth={760}>
          <Table verticalSpacing="sm">
            <Table.Thead>
              <Table.Tr>
                <Table.Th>{t('editor.where')}</Table.Th>
                <Table.Th>{t('editor.now')}</Table.Th>
                <Table.Th>{t('editor.change')}</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {rows.map((row) => {
                const draft = drafts[row.id] ?? [...row.now.codes];
                return (
                  <Table.Tr key={row.id}>
                    <Table.Td fw={600}>{row.label}</Table.Td>
                    <Table.Td>
                      <Text size="sm">{list(row.now.codes, t('questions.unassigned'))}</Text>
                      <Text size="xs" c="dimmed">
                        {t(`layers.source.${row.now.source}`)}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      <Group align="flex-end">
                        <MultiSelect
                          aria-label={t('editor.positionsFor', { where: row.label })}
                          data={options}
                          value={draft}
                          onChange={(value) => setDrafts({ ...drafts, [row.id]: value })}
                          miw={220}
                        />
                        <Button
                          size="sm"
                          disabled={draft.length === 0 || draft.join() === row.now.codes.join()}
                          loading={set.isPending && set.variables?.p_vessel_id === row.vesselId}
                          onClick={() =>
                            set.mutate({
                              p_company_id: company.id,
                              p_vessel_id: row.vesselId,
                              p_question_key: question.key,
                              p_position_codes: draft,
                            })
                          }
                        >
                          {t('editor.save')}
                        </Button>
                        {row.own ? (
                          <Button
                            size="sm"
                            variant="default"
                            loading={
                              revert.isPending && revert.variables?.p_vessel_id === row.vesselId
                            }
                            onClick={() => {
                              setDrafts({ ...drafts, [row.id]: [] });
                              revert.mutate({
                                p_company_id: company.id,
                                p_vessel_id: row.vesselId,
                                p_question_key: question.key,
                              });
                            }}
                          >
                            {row.vesselId === null
                              ? t('editor.returnBase')
                              : t('editor.returnCompany')}
                          </Button>
                        ) : null}
                      </Group>
                    </Table.Td>
                  </Table.Tr>
                );
              })}
            </Table.Tbody>
          </Table>
        </Table.ScrollContainer>
        <SaveError error={set.error ?? revert.error} />
      </Stack>
    </Card>
  );
}

function FlagsEditor({ question, layers }: { question: Question; layers: Layers }) {
  const { t } = useTranslation(['dashboard', 'common']);
  const { company, access } = useWorkplace();
  const current = layers.flags.get(question.key) ?? {
    workTypes: question.workTypes,
    prepTiming: question.prepTiming,
  };
  const resolved = flagsOf(question, layers);
  const [work, setWork] = useState<RisqWorkType[]>([...current.workTypes]);
  const [timing, setTiming] = useState<RisqPrepTiming[]>([...current.prepTiming]);
  const refresh = [...refreshLayers(company.id, question.key), ['content']] as const;
  const set = useRpc('risq_set_question_flags', refresh);
  const revert = useRpc('risq_revert_question_flags', refresh);
  const toggle = <T,>(list: T[], value: T) =>
    list.includes(value) ? list.filter((v) => v !== value) : [...list, value];
  // The hidden "Company" type (D72) stays as it was.
  const workToSave = [
    ...work.filter((w) => w !== 'company'),
    ...(current.workTypes.includes('company') ? (['company'] as const) : []),
  ];
  const args = (companyId: string | null) => ({
    p_company_id: companyId,
    p_question_key: question.key,
    p_work_types: workToSave,
    p_prep_timing: timing,
  });
  return (
    <Card withBorder>
      <Stack>
        <Title order={2} size="h3">
          {t('editor.flagsTitle')}
        </Title>
        <Text size="sm">
          {t('editor.flagsSource', { source: t(`layers.source.${resolved.source}`) })}
        </Text>
        <Group align="flex-start" grow>
          <Stack gap="xs">
            <Text fw={600}>{t('questions.workTypes')}</Text>
            {VISIBLE_WORK_TYPES.map((w) => (
              <Checkbox
                key={w}
                label={t(`common:workTypes.${w}.name`)}
                checked={work.includes(w)}
                onChange={() => setWork(toggle(work, w))}
              />
            ))}
          </Stack>
          <Stack gap="xs">
            <Text fw={600}>{t('questions.timings')}</Text>
            {RISQ_PREP_TIMINGS.map((p) => (
              <Checkbox
                key={p}
                label={t('questions.timingOption', {
                  short: t(`common:timings.${p}.short`),
                  meaning: t(`common:timings.${p}.meaning`),
                })}
                checked={timing.includes(p)}
                onChange={() => setTiming(toggle(timing, p))}
              />
            ))}
            {timing.length === 0 ? (
              <Text size="sm" c="dimmed">
                {t('editor.noTimingHint')}
              </Text>
            ) : null}
          </Stack>
        </Group>
        <Group>
          <Button
            loading={set.isPending && set.variables?.p_company_id !== null}
            onClick={() => set.mutate(args(company.id))}
          >
            {t('editor.saveForCompany')}
          </Button>
          {layers.flags.has(question.key) ? (
            <Button
              variant="default"
              loading={revert.isPending}
              onClick={() =>
                revert.mutate({ p_company_id: company.id, p_question_key: question.key })
              }
            >
              {t('editor.returnBase')}
            </Button>
          ) : null}
          {access.isAdmin ? (
            <Button
              variant="outline"
              color="red"
              loading={set.isPending && set.variables?.p_company_id === null}
              onClick={() => set.mutate(args(null))}
            >
              {t('editor.saveBase')}
            </Button>
          ) : null}
        </Group>
        {access.isAdmin ? (
          <Text size="xs" c="dimmed">
            {t('editor.baseHint')}
          </Text>
        ) : null}
        <SaveError error={set.error ?? revert.error} />
      </Stack>
    </Card>
  );
}

function ChecklistEditor({ question }: { question: Question }) {
  const { t } = useTranslation('dashboard');
  const { company } = useWorkplace();
  const data = useEditorData(question);
  const refresh = refreshLayers(company.id, question.key);
  const setPoint = useRpc('risq_set_wtc_item', refresh);
  const addPoint = useRpc('risq_add_wtc_item', refresh);
  const revertPoint = useRpc('risq_revert_wtc_item', refresh);
  const [editing, setEditing] = useState<{ key: string; text: string } | null>(null);
  const [newText, setNewText] = useState('');
  const error = setPoint.error ?? addPoint.error ?? revertPoint.error;
  return (
    <Card withBorder>
      <Stack>
        <Title order={2} size="h3">
          {t('editor.checklistTitle')}
        </Title>
        <Text size="sm">{t('editor.checklistHint')}</Text>
        <QueryState loading={data.isPending} error={data.error}>
          {() => {
            const d = data.data;
            if (d === undefined) {
              return null;
            }
            const changes = new Map(
              d.companyPoints.filter((c) => !c.is_own).map((c) => [c.item_key, c]),
            );
            const own = d.companyPoints.filter((c) => c.is_own);
            const editor = (key: string, save: (text: string) => void) =>
              editing?.key === key ? (
                <Stack gap="xs">
                  <Textarea
                    aria-label={t('editor.pointText')}
                    autosize
                    minRows={2}
                    value={editing.text}
                    onChange={(e) => setEditing({ key, text: e.currentTarget.value })}
                  />
                  <Group>
                    <Button
                      size="xs"
                      disabled={editing.text.trim() === ''}
                      onClick={() => {
                        save(editing.text);
                        setEditing(null);
                      }}
                    >
                      {t('editor.save')}
                    </Button>
                    <Button size="xs" variant="default" onClick={() => setEditing(null)}>
                      {t('common.cancel')}
                    </Button>
                  </Group>
                </Stack>
              ) : null;
            return (
              <Stack>
                {d.points.length === 0 ? <Text size="sm">{t('editor.noBasePoints')}</Text> : null}
                {d.points.map((p) => {
                  const change = changes.get(p.item_key);
                  const hidden = change?.hidden === true;
                  const text = change?.text_en ?? p.text_en;
                  return (
                    <Card key={p.id} withBorder padding="sm" bg={hidden ? 'gray.0' : undefined}>
                      <Stack gap="xs">
                        <Group gap="xs">
                          {change === undefined ? (
                            <Badge variant="outline" color="gray" tt="none">
                              {t('layers.source.base')}
                            </Badge>
                          ) : null}
                          {change?.text_en != null ? (
                            <Badge variant="light" tt="none">
                              {t('editor.changedText')}
                            </Badge>
                          ) : null}
                          {hidden ? (
                            <Badge variant="light" color="orange" tt="none">
                              {t('editor.hidden')}
                            </Badge>
                          ) : null}
                        </Group>
                        <Text size="sm" td={hidden ? 'line-through' : undefined}>
                          {text}
                        </Text>
                        {change?.text_en != null ? (
                          <Text size="xs" c="dimmed">
                            {t('editor.baseText', { text: p.text_en })}
                          </Text>
                        ) : null}
                        {editor(p.item_key, (value) =>
                          setPoint.mutate({
                            p_company_id: company.id,
                            p_question_key: question.key,
                            p_item_key: p.item_key,
                            p_text_en: value,
                            p_hidden: hidden,
                          }),
                        )}
                        <Group gap="xs">
                          <Button
                            size="xs"
                            variant="default"
                            onClick={() => setEditing({ key: p.item_key, text })}
                          >
                            {t('editor.editText')}
                          </Button>
                          <Button
                            size="xs"
                            variant="default"
                            onClick={() =>
                              hidden && change?.text_en == null
                                ? revertPoint.mutate({
                                    p_company_id: company.id,
                                    p_question_key: question.key,
                                    p_item_key: p.item_key,
                                  })
                                : setPoint.mutate({
                                    p_company_id: company.id,
                                    p_question_key: question.key,
                                    p_item_key: p.item_key,
                                    p_text_en: change?.text_en ?? null,
                                    p_hidden: !hidden,
                                  })
                            }
                          >
                            {hidden ? t('editor.show') : t('editor.hide')}
                          </Button>
                          {change === undefined ? null : (
                            <Button
                              size="xs"
                              variant="subtle"
                              onClick={() =>
                                revertPoint.mutate({
                                  p_company_id: company.id,
                                  p_question_key: question.key,
                                  p_item_key: p.item_key,
                                })
                              }
                            >
                              {t('editor.returnBase')}
                            </Button>
                          )}
                        </Group>
                      </Stack>
                    </Card>
                  );
                })}
                <Text fw={600}>{t('editor.ownPoints')}</Text>
                {own.length === 0 ? <Text size="sm">{t('editor.noOwnPoints')}</Text> : null}
                {own.map((p) => (
                  <Card key={p.id} withBorder padding="sm">
                    <Stack gap="xs">
                      <Badge variant="light" color="teal" tt="none" w="fit-content">
                        {t('editor.ownPoint')}
                      </Badge>
                      <Text size="sm">{p.text_en}</Text>
                      {editor(p.item_key, (value) =>
                        setPoint.mutate({
                          p_company_id: company.id,
                          p_question_key: question.key,
                          p_item_key: p.item_key,
                          p_text_en: value,
                          p_hidden: false,
                        }),
                      )}
                      <Group gap="xs">
                        <Button
                          size="xs"
                          variant="default"
                          onClick={() => setEditing({ key: p.item_key, text: p.text_en ?? '' })}
                        >
                          {t('editor.editText')}
                        </Button>
                        <Button
                          size="xs"
                          variant="subtle"
                          color="red"
                          onClick={() =>
                            revertPoint.mutate({
                              p_company_id: company.id,
                              p_question_key: question.key,
                              p_item_key: p.item_key,
                            })
                          }
                        >
                          {t('editor.remove')}
                        </Button>
                      </Group>
                    </Stack>
                  </Card>
                ))}
                <Textarea
                  label={t('editor.newPoint')}
                  autosize
                  minRows={2}
                  value={newText}
                  onChange={(e) => setNewText(e.currentTarget.value)}
                />
                <Button
                  w="fit-content"
                  disabled={newText.trim() === ''}
                  loading={addPoint.isPending}
                  onClick={() =>
                    addPoint.mutate(
                      {
                        p_company_id: company.id,
                        p_question_key: question.key,
                        p_text_en: newText,
                      },
                      { onSuccess: () => setNewText('') },
                    )
                  }
                >
                  {t('editor.addPoint')}
                </Button>
              </Stack>
            );
          }}
        </QueryState>
        <SaveError error={error} />
      </Stack>
    </Card>
  );
}

const NOTE_KINDS = ['ims_reference', 'office_remark'] as const;

function NotesEditor({ question }: { question: Question }) {
  const { t } = useTranslation('dashboard');
  const { company } = useWorkplace();
  const data = useEditorData(question);
  const refresh = refreshLayers(company.id, question.key);
  const save = useRpc('risq_set_company_note', refresh);
  const remove = useRpc('risq_remove_company_note', refresh);
  const [kind, setKind] = useState<'ims_reference' | 'office_remark'>('ims_reference');
  const [text, setText] = useState('');
  const [editing, setEditing] = useState<{ id: string; text: string } | null>(null);
  const kindOptions = NOTE_KINDS.map((k) => ({
    value: k,
    label: k === 'ims_reference' ? t('notes.ims') : t('notes.remarks'),
  }));
  return (
    <Card withBorder>
      <Stack>
        <Title order={2} size="h3">
          {t('editor.notesTitle')}
        </Title>
        <Text size="sm">{t('editor.notesHint')}</Text>
        <QueryState loading={data.isPending} error={data.error}>
          {() => (
            <Stack>
              {NOTE_KINDS.map((k) => {
                const notes = (data.data?.notes ?? []).filter((n) => n.kind === k);
                return (
                  <Stack key={k} gap="xs">
                    <Text fw={600}>
                      {k === 'ims_reference' ? t('notes.ims') : t('notes.remarks')}
                    </Text>
                    {notes.length === 0 ? <Text size="sm">{t('notes.none')}</Text> : null}
                    {notes.map((n) => (
                      <Card key={n.id} withBorder padding="sm">
                        {editing?.id === n.id ? (
                          <Stack gap="xs">
                            <Textarea
                              aria-label={t('editor.noteText')}
                              autosize
                              value={editing.text}
                              onChange={(e) =>
                                setEditing({ id: n.id, text: e.currentTarget.value })
                              }
                            />
                            <Group>
                              <Button
                                size="xs"
                                onClick={() => {
                                  save.mutate({
                                    p_company_id: company.id,
                                    p_question_key: question.key,
                                    p_kind: n.kind,
                                    p_text: editing.text,
                                    p_note_id: n.id,
                                  });
                                  setEditing(null);
                                }}
                              >
                                {t('editor.save')}
                              </Button>
                              <Button size="xs" variant="default" onClick={() => setEditing(null)}>
                                {t('common.cancel')}
                              </Button>
                            </Group>
                          </Stack>
                        ) : (
                          <Group justify="space-between" align="flex-start" wrap="nowrap">
                            <Stack gap={2}>
                              <Text size="sm">{n.text}</Text>
                              <Text size="xs" c="dimmed">
                                {n.origin === 'import'
                                  ? t('editor.fromMasterFile')
                                  : t('editor.addedByOffice')}
                              </Text>
                            </Stack>
                            {n.origin === 'manual' ? (
                              <Group gap="xs" wrap="nowrap">
                                <Button
                                  size="xs"
                                  variant="default"
                                  onClick={() => setEditing({ id: n.id, text: n.text })}
                                >
                                  {t('editor.editText')}
                                </Button>
                                <Button
                                  size="xs"
                                  variant="subtle"
                                  color="red"
                                  onClick={() => remove.mutate({ p_note_id: n.id })}
                                >
                                  {t('editor.remove')}
                                </Button>
                              </Group>
                            ) : null}
                          </Group>
                        )}
                      </Card>
                    ))}
                  </Stack>
                );
              })}
              <Group align="flex-end">
                <Select
                  label={t('editor.noteKind')}
                  data={kindOptions}
                  value={kind}
                  allowDeselect={false}
                  onChange={(value) =>
                    setKind(value === 'office_remark' ? 'office_remark' : 'ims_reference')
                  }
                />
                <TextInput
                  label={t('editor.noteText')}
                  value={text}
                  onChange={(e) => setText(e.currentTarget.value)}
                  miw={320}
                />
                <Button
                  disabled={text.trim() === ''}
                  loading={save.isPending}
                  onClick={() =>
                    save.mutate(
                      {
                        p_company_id: company.id,
                        p_question_key: question.key,
                        p_kind: kind,
                        p_text: text,
                        p_note_id: null,
                      },
                      { onSuccess: () => setText('') },
                    )
                  }
                >
                  {t('editor.addNote')}
                </Button>
              </Group>
              <Text size="xs" c="dimmed">
                {kind === 'ims_reference' ? t('editor.imsAudience') : t('editor.remarkAudience')}
              </Text>
            </Stack>
          )}
        </QueryState>
        <SaveError error={save.error ?? remove.error} />
      </Stack>
    </Card>
  );
}

/** D13 Content editor (DPA, Admin): one question's company and vessel layers, notes, history. */
export function ContentEditorPage() {
  const { t } = useTranslation(['dashboard', 'common']);
  const { questionKey } = useParams();
  const navigate = useNavigate();
  const content = useContent();
  const layers = useLayers();
  const [search, setSearch] = useState('');
  const questions = useMemo(() => content.data?.questions ?? [], [content.data]);
  const found = useMemo(() => {
    const s = search.trim().toLowerCase();
    return s === ''
      ? questions
      : questions.filter(
          (q) => q.code.toLowerCase().startsWith(s) || q.textEn.toLowerCase().includes(s),
        );
  }, [questions, search]);
  const question = questions.find((q) => q.key === questionKey) ?? null;

  return (
    <>
      <PageTitle title={t('editor.title')} />
      <QueryState
        loading={content.isPending || layers.isPending}
        error={content.error ?? layers.error}
      >
        {() => (
          <Grid gap="lg">
            <Grid.Col span={{ base: 12, md: 4, xl: 3 }}>
              <Card withBorder>
                <Stack gap="xs">
                  <TextInput
                    label={t('questions.search')}
                    placeholder={t('questions.searchHint')}
                    value={search}
                    onChange={(e) => setSearch(e.currentTarget.value)}
                  />
                  <Text size="sm">{t('questions.count', { count: found.length })}</Text>
                  <ScrollArea.Autosize mah={640}>
                    <Stack gap={2}>
                      {found.map((q) => (
                        <Anchor
                          key={q.key}
                          component={Link}
                          to={`/content/${encodeURIComponent(q.key)}`}
                          fw={q.key === questionKey ? 700 : 400}
                          size="sm"
                          aria-current={q.key === questionKey ? 'page' : undefined}
                        >
                          {t('question.codeText', { code: q.code, text: q.textEn.slice(0, 60) })}
                        </Anchor>
                      ))}
                    </Stack>
                  </ScrollArea.Autosize>
                </Stack>
              </Card>
            </Grid.Col>
            <Grid.Col span={{ base: 12, md: 8, xl: 9 }}>
              {question === null || layers.data === undefined ? (
                <Stack>
                  <Text>{t('editor.choose')}</Text>
                  {questions[0] === undefined ? null : (
                    <Button
                      w="fit-content"
                      onClick={() =>
                        void navigate(`/content/${encodeURIComponent(questions[0]?.key ?? '')}`)
                      }
                    >
                      {t('editor.openFirst')}
                    </Button>
                  )}
                </Stack>
              ) : (
                <Stack gap="lg" key={question.key}>
                  <Card withBorder>
                    <Stack gap="xs">
                      <Title order={2}>{t('question.title', { code: question.code })}</Title>
                      <Text>{question.textEn}</Text>
                      {question.adviceEn === null ? null : (
                        <Alert color="blue" title={t('question.advice')}>
                          {question.adviceEn}
                        </Alert>
                      )}
                      <Text size="xs" c="dimmed">
                        {t('editor.keyHint', { key: question.key })}
                      </Text>
                    </Stack>
                  </Card>
                  <PositionsEditor question={question} layers={layers.data} />
                  <FlagsEditor question={question} layers={layers.data} />
                  <ChecklistEditor question={question} />
                  <NotesEditor question={question} />
                  <LayerHistory questionKey={question.key} />
                </Stack>
              )}
            </Grid.Col>
          </Grid>
        )}
      </QueryState>
    </>
  );
}
